import { DecimalUtil } from '@orca-so/common-sdk'
import {
  type ITradeDataInfo,
  type OfferDataInfo,
} from '@renec-foundation/escrow-sdk'
import moment from 'moment'
import { useCallback } from 'react'

import { TRADE_DATE_FORMAT } from '@/constants/index'
import useTokens from '@/hooks/use-tokens'
import { notify } from '@/utils/apis/helper'
import { getAddressUrl,getTransactionDetailUrl } from '@/utils/helpers'
import { format } from '@/utils/numeric'

type NotifyParams = {
  txid: string,
  category: string,
  tradeOrderData: ITradeDataInfo
}

type OfferNotificationParams = {
  txid: string,
  category: string,
  offerData: OfferDataInfo
}

const useNotifyToInternalTeam = () => {
  const { fiatTokensMap, fiatCustomInfoList } = useTokens()

  const sendNotice = useCallback((params: NotifyParams) => {
    const {
      txid,
      category,
      tradeOrderData: {
        tradeId,
        pubkey,
        tokenMint,
        tradeAmount: tokenAmount,
        fiatAmount,
        sellerBondType,
        buyerBondType,
        buyerAuthority,
        sellerAuthority,
        createdAt,
        sellOfferId,
        buyOfferId,
        fiatCurrency,
      },
    } = params

    const tokenInfo = fiatTokensMap.get(tokenMint.toBase58())
    const formattedCryptoAmount = `${format(
      DecimalUtil.fromU64(tokenAmount, tokenInfo?.decimals),
      tokenInfo?.decimals,
    )} ${tokenInfo?.symbol}`

    const fiatInfo = fiatCustomInfoList.find(
      (t) => t.displaySymbol?.toLowerCase() === fiatCurrency.toLowerCase(),
    )
    const formattedFiatAmount = `${format(fiatAmount, fiatInfo?.decimals)} ${fiatCurrency}`

    const buyerAddress = buyerAuthority.toBase58()
    const tradeDetails = [
      '*Trade detail:*',
      ...(sellOfferId ? [`Sell Offer ID: #${sellOfferId?.toNumber()}`] : []),
      ...(buyOfferId ? [`Buy Offer ID: #${buyOfferId?.toNumber()}`] : []),
      `Sell *\`${formattedCryptoAmount}\`* for *\`${formattedFiatAmount}\`*`,
      `Bond type of seller: ${Object.keys(sellerBondType)[0]}`,
      `Seller address: ${sellerAuthority.toBase58()}`,
      ...(buyerAddress !== '11111111111111111111111111111111'
        ? [
          `Buyer address: ${buyerAddress}`,
          `Bond type of buyer: ${Object.keys(buyerBondType)[0]}`,
        ] : []
      ),
      `Created at: ${moment(createdAt.muln(1000).toNumber()).format(TRADE_DATE_FORMAT)}`,
    ]

    const notificationPayload = {
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: `[${category}] trade #${tradeId.toString()}`,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: tradeDetails.join('\n• '),
            },
          ],
        },
        ...(pubkey ? [{
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `<${getAddressUrl(pubkey.toBase58())}|View detail on Explorer>`,
            },
          ],
        }] : []),
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `<${getTransactionDetailUrl(txid)}|View TX on Explorer>`,
            },
          ],
        },
      ],
    }

    notify(notificationPayload)
  }, [
    fiatTokensMap,
    fiatCustomInfoList,
  ])

  const sendOfferNotice = useCallback((params: OfferNotificationParams) => {
    const {
      txid,
      category,
      offerData: {
        offerId,
        pubkey,
        tokenMint,
        totalAmount,
        bondType,
        makerAuthority,
        createdAt,
        fiatCurrency,
        rate,
      },
    } = params
    const tokenInfo = fiatTokensMap.get(tokenMint.toBase58())
    const formattedCryptoAmount = `${format(
      DecimalUtil.fromU64(totalAmount, tokenInfo?.decimals),
      tokenInfo?.decimals,
    )} ${tokenInfo?.symbol}`

    const fiatAmount = totalAmount.muln(rate)
    const formattedFiatAmount = `${format(
      DecimalUtil.fromU64(fiatAmount, tokenInfo?.decimals),
      tokenInfo?.decimals,
    )} ${fiatCurrency}`

    const offerDetails = [
      '*Offer detail:*',
      `Sell *\`${formattedCryptoAmount}\`* for *\`${formattedFiatAmount}\`*`,
      `Bond type: ${Object.keys(bondType)[0]}`,
      `Seller address: ${makerAuthority.toBase58()}`,
      `created at: ${moment(createdAt.muln(1000).toNumber()).format(TRADE_DATE_FORMAT)}`,
    ]

    const notificationPayload = {
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: `[${category}] offer #${offerId.toString()}`,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: offerDetails.join('\n• '),
            },
          ],
        },
        ...(pubkey ? [{
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `<${getAddressUrl(pubkey.toBase58())}|View offer detail on Explorer>`,
            },
          ],
        }] : []),
        ...(txid ? [{
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `<${getTransactionDetailUrl(txid)}|View TX on Explorer>`,
            },
          ],
        }] : []),
      ],
    }

    notify(notificationPayload)
  }, [fiatTokensMap])

  return {
    sendNotice,
    sendOfferNotice,
  }
}

export default useNotifyToInternalTeam
